import { useAppDispatch, useAppSelector } from "@app/hooks";
import useSimpleConfirmation from "@hooks/useSimpleConfirmation";
import { useUpdateSectionMutation } from "@services/survey-sections";
import { updateCurrentSurvey } from "@survey/surveySlice";
import { useEffect, useState, type FocusEvent } from "react";

const RESERVED_SECTION_NAMES = [
  "cover",
  "lease comp",
  "lease comps",
  "lease comparables",
  "lease comparison",
  "property comparison",
  "property comps",
  "property comparables",
  "market map",
  "properties",
  "tour schedule",
];

const isReservedSectionName = (name: string): boolean => {
  return RESERVED_SECTION_NAMES.includes(name.trim().toLowerCase());
};

const RESERVED_NAME_ERROR =
  "Please use a section title that is not similar to an existing Survey Section";

export const useSectionEditing = () => {
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [titleError, setTitleError] = useState<string | null>(null);
  const [lastCreatedSectionId, setLastCreatedSectionId] = useState<
    string | null
  >(null);

  const dispatch = useAppDispatch();
  const [updateSection] = useUpdateSectionMutation();
  const { quickDelete } = useSimpleConfirmation();
  const storeSections = useAppSelector(
    (state) => state.survey?.currentSurvey?.sections,
  );

  // Sync local editing state when section data changes from Redux
  useEffect(() => {
    if (editingSection && storeSections) {
      const currentSection = storeSections.find(
        (section) => section.surveySectionId === editingSection,
      );
      if (currentSection?.name !== undefined) {
        setEditingName(currentSection.name);
      }
    }
  }, [editingSection, storeSections]);

  // Start editing a section
  const handleStartEditing = (sectionId: string, currentName: string) => {
    setEditingSection(sectionId);
    setEditingName(currentName);
  };

  // Handle section name change during editing
  const handleSectionNameChange = (sectionId: string, value: string) => {
    setEditingSection(sectionId);
    setEditingName(value);
    if (isReservedSectionName(value)) {
      setTitleError(RESERVED_NAME_ERROR);
    } else {
      setTitleError(null);
    }
  };

  // Save section edit
  const handleSaveEdit = async (
    sectionId: string,
    e?: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (!storeSections) return;

    // Find the current section to get its current name
    const currentSection = storeSections.find(
      (section) => section.surveySectionId === sectionId,
    );
    if (!currentSection) return;

    // Get the current value from the event target, fallback to editingName if no event
    const currentValue = e?.target?.value ?? editingName;

    // Block save if the name matches a reserved section name
    if (isReservedSectionName(currentValue)) {
      setTitleError(RESERVED_NAME_ERROR);
      return;
    }
    setTitleError(null);

    // If the field is empty when user saves it, provide a default name
    const finalName =
      currentValue.trim() === "" ? "Untitled Section" : currentValue.trim();

    // Only update if the name actually changed
    if (finalName === currentSection.name) {
      // Just clear editing state if no change
      setEditingSection(null);
      setEditingName("");
      return;
    }

    // Optimistic update: Update Redux store immediately
    const updatedSections = storeSections.map((section) =>
      section.surveySectionId === sectionId
        ? { ...section, name: finalName }
        : section,
    );
    dispatch(updateCurrentSurvey({ sections: updatedSections }));

    // Clear editing state immediately for better UX
    setEditingSection(null);
    setEditingName("");

    updateSection({
      sectionId: sectionId,
      updates: { name: finalName },
    })
      .unwrap()
      .catch((_error) => {
        console.error("Failed to update section name");
        // Revert optimistic update on error
        dispatch(updateCurrentSurvey({ sections: storeSections }));
        // Restart editing mode on error
        setEditingSection(sectionId);
        setEditingName(finalName);
      });
  };

  // Cancel section edit
  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditingName("");
    setTitleError(null);
  };

  // Handle new section creation result
  const handleNewSectionCreated = (sectionId: string | null) => {
    if (sectionId) {
      setLastCreatedSectionId(sectionId);
    }
  };

  // Clear lastCreatedSectionId after component has rendered with the new section
  useEffect(() => {
    if (lastCreatedSectionId) {
      const timer = setTimeout(() => {
        setLastCreatedSectionId(null);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [lastCreatedSectionId]);

  // Request confirmation for section deletion
  const requestDeleteConfirmation = async (
    sectionName: string,
  ): Promise<boolean> => {
    return await quickDelete({
      title: sectionName,
      text: `This will remove the section and all uploaded files. Are you sure you want to delete this section?`,
    });
  };

  return {
    editingSection,
    editingName,
    titleError,
    lastCreatedSectionId,
    setEditingName,
    handleStartEditing,
    handleSectionNameChange,
    handleSaveEdit,
    handleCancelEdit,
    handleNewSectionCreated,
    requestDeleteConfirmation,
  };
};
